import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import { fmtDateTime } from '../../../utils/ruDates';
import './AdminOps.css';

const AREAS = {
    server: 'Сервер',
    database: 'База данных',
    syncs: 'Фоновые задачи',
    support: 'Техподдержка',
    collector: 'Сборщики',
};

const SEVERITY = {
    critical: 'Критично',
    warning: 'Внимание',
    info: 'Инфо',
};

const STATUS_FILTERS = [
    { value: 'open', label: 'Открытые' },
    { value: 'new', label: 'Новые' },
    { value: 'acked', label: 'Принятые' },
    { value: 'resolved', label: 'Закрытые' },
    { value: '', label: 'Все' },
];

const STATUS_LABELS = { new: 'новый', acked: 'принят', resolved: 'закрыт' };

// Ключевые метрики области: [ключ, подпись, единица, порог предупреждения]
const KEY_METRICS = {
    server: [
        ['disk_used_pct', 'Диск занят', '%', 80],
        ['disk_free_gb', 'Диск свободно', 'ГБ'],
        ['mem_used_pct', 'Память занята', '%', 85],
        ['load_15m', 'Нагрузка (15 мин)', ''],
    ],
    database: [
        ['db_size_gb', 'Размер базы', 'ГБ'],
        ['connections_total', 'Соединений', ''],
        ['oldest_xact_minutes', 'Самая долгая транзакция', 'мин', 30],
    ],
    syncs: [
        ['jobs_stuck', 'Зависших задач', '', 1],
    ],
    support: [
        ['waiting_reply', 'Ждут ответа', '', 1],
        ['oldest_wait_hours', 'Дольше всех ждёт', 'ч', 12],
        ['tickets_new_24h', 'Новых за сутки', ''],
    ],
};

function fmtNumber(value) {
    if (value === null || value === undefined) return '—';
    if (typeof value !== 'number') return String(value);
    return value.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
}

function Sparkline({ points }) {
    if (!points || points.length < 2) return null;
    const w = 120;
    const h = 28;
    const min = Math.min(...points);
    const max = Math.max(...points);
    const span = max - min || 1;
    const d = points
        .map((v, i) => `${(i / (points.length - 1)) * w},${h - 2 - ((v - min) / span) * (h - 4)}`)
        .join(' ');
    return (
        <svg className="aops-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
            <polyline points={d} fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    );
}

function SignalRow({ signal, onStatus, busy }) {
    const [open, setOpen] = useState(false);
    const hasDetails = signal.details && Object.keys(signal.details).length > 0;
    return (
        <li className={`aops-signal aops-signal--${signal.severity} aops-signal--${signal.status}`}>
            <div className="aops-signal__main">
                <span className={`aops-sev aops-sev--${signal.severity}`}>{SEVERITY[signal.severity] || signal.severity}</span>
                <div className="aops-signal__body">
                    <button type="button" className="aops-signal__title" onClick={() => setOpen((v) => !v)}>
                        {signal.title}
                    </button>
                    <div className="aops-muted">
                        {AREAS[signal.area] || signal.area}
                        {' · '}{STATUS_LABELS[signal.status] || signal.status}
                        {' · '}впервые {fmtDateTime(signal.first_seen_at)}
                        {' · '}последний раз {fmtDateTime(signal.last_seen_at)}
                        {signal.occurrences > 1 && ` · замечен ${signal.occurrences} раз`}
                        {signal.resolved_by && ` · закрыт ${signal.resolved_by === 'auto' ? 'автоматически' : 'вручную'}`}
                    </div>
                </div>
                <div className="aops-signal__actions">
                    {signal.status === 'new' && (
                        <button type="button" className="aops-button" disabled={busy} onClick={() => onStatus(signal.id, 'acked')}>Принять</button>
                    )}
                    {signal.status !== 'resolved' && (
                        <button
                            type="button"
                            className="aops-button"
                            disabled={busy}
                            title="Если проблема останется, сигнал откроется снова при следующем обходе"
                            onClick={() => onStatus(signal.id, 'resolved')}
                        >
                            Закрыть
                        </button>
                    )}
                    {signal.status !== 'new' && (
                        <button type="button" className="aops-button" disabled={busy} onClick={() => onStatus(signal.id, 'new')}>Вернуть</button>
                    )}
                </div>
            </div>
            {open && hasDetails && <pre className="aops-pre">{JSON.stringify(signal.details, null, 2)}</pre>}
        </li>
    );
}

function SignalsTab() {
    const { request } = useApi();
    const [status, setStatus] = useState('open');
    const [area, setArea] = useState('');
    const [items, setItems] = useState([]);
    const [openCounts, setOpenCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status) params.set('status', status);
            if (area) params.set('area', area);
            const data = await request(`/api/v1/admin/ops/signals?${params.toString()}`);
            setItems(data?.items || []);
            setOpenCounts(data?.open_counts || {});
        } catch (error) {
            logger.error('Failed to load ops signals', error);
        } finally {
            setLoading(false);
        }
    }, [request, status, area]);

    useEffect(() => { load(); }, [load]);

    const changeStatus = async (id, next) => {
        setBusy(true);
        try {
            await request(`/api/v1/admin/ops/signals/${id}/status`, {
                method: 'POST',
                body: JSON.stringify({ status: next }),
            });
            await load();
        } catch (error) {
            logger.error('Failed to update ops signal', error);
        } finally {
            setBusy(false);
        }
    };

    const areaCount = (key) => {
        const c = openCounts[key] || {};
        return Object.values(c).reduce((a, b) => a + b, 0);
    };

    return (
        <>
            <div className="aops-filters">
                {STATUS_FILTERS.map((f) => (
                    <button
                        key={f.value || 'all'}
                        type="button"
                        className={`aops-chip ${status === f.value ? 'aops-chip--active' : ''}`}
                        onClick={() => setStatus(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            <div className="aops-filters">
                <button type="button" className={`aops-chip ${area === '' ? 'aops-chip--active' : ''}`} onClick={() => setArea('')}>
                    Все области
                </button>
                {Object.entries(AREAS).map(([key, label]) => {
                    const n = areaCount(key);
                    const critical = openCounts[key]?.critical;
                    return (
                        <button
                            key={key}
                            type="button"
                            className={`aops-chip ${area === key ? 'aops-chip--active' : ''}`}
                            onClick={() => setArea(key)}
                        >
                            {label}
                            {n > 0 && <span className={`aops-count ${critical ? 'aops-count--critical' : ''}`}>{n}</span>}
                        </button>
                    );
                })}
            </div>
            {items.length === 0 && !loading && <div className="aops-empty">Сигналов нет.</div>}
            <ul className="aops-signals">
                {items.map((s) => <SignalRow key={s.id} signal={s} onStatus={changeStatus} busy={busy} />)}
            </ul>
        </>
    );
}

function MetricsTab() {
    const { request } = useApi();
    const [latest, setLatest] = useState({});
    const [history, setHistory] = useState({});
    const [loading, setLoading] = useState(false);
    const [collecting, setCollecting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await request('/api/v1/admin/ops/snapshots/latest');
            setLatest(data?.items || {});
            const areas = Object.keys(KEY_METRICS);
            const hist = await Promise.all(
                areas.map((a) => request(`/api/v1/admin/ops/snapshots?area=${a}&hours=168`).catch(() => null)),
            );
            setHistory(Object.fromEntries(areas.map((a, i) => [a, hist[i]?.items || []])));
        } catch (error) {
            logger.error('Failed to load ops snapshots', error);
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => { load(); }, [load]);

    const collectNow = async () => {
        setCollecting(true);
        try {
            await request('/api/v1/admin/ops/collect', { method: 'POST' });
            await load();
        } catch (error) {
            logger.error('Failed to run ops collection', error);
        } finally {
            setCollecting(false);
        }
    };

    return (
        <>
            <div className="aops-toolbar">
                <span className="aops-muted">Сборщики обходят систему раз в 15 минут. Графики — за 7 дней.</span>
                <button type="button" className="aops-button" onClick={collectNow} disabled={collecting || loading}>
                    {collecting ? 'Собираю…' : 'Собрать сейчас'}
                </button>
            </div>
            <div className="aops-cards">
                {Object.entries(KEY_METRICS).map(([area, metrics]) => {
                    const snap = latest[area];
                    const series = history[area] || [];
                    return (
                        <section key={area} className="aops-card">
                            <header className="aops-card__head">
                                <h3>{AREAS[area]}</h3>
                                <span className="aops-muted">{snap ? fmtDateTime(snap.collected_at) : 'нет данных'}</span>
                            </header>
                            {snap?.error && <div className="aops-error">Сборщик упал: {snap.error}</div>}
                            {snap && !snap.error && (
                                <ul className="aops-metrics">
                                    {metrics.map(([key, label, unit, warn]) => {
                                        const value = snap.metrics[key];
                                        const bad = warn !== undefined && typeof value === 'number' && value >= warn;
                                        const points = series
                                            .map((s) => s.metrics?.[key])
                                            .filter((v) => typeof v === 'number');
                                        return (
                                            <li key={key} className={`aops-metric ${bad ? 'aops-metric--bad' : ''}`}>
                                                <span className="aops-metric__label">{label}</span>
                                                <Sparkline points={points} />
                                                <span className="aops-metric__value">{fmtNumber(value)}{unit && ` ${unit}`}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {snap && !snap.error && (
                                <details className="aops-raw">
                                    <summary>Все метрики</summary>
                                    <pre className="aops-pre">{JSON.stringify(snap.metrics, null, 2)}</pre>
                                </details>
                            )}
                        </section>
                    );
                })}
            </div>
        </>
    );
}

const TABS = [
    { key: 'signals', label: 'Сигналы', Component: SignalsTab },
    { key: 'metrics', label: 'Метрики', Component: MetricsTab },
];

function AdminOps() {
    const [tab, setTab] = useState('signals');
    const Active = TABS.find((t) => t.key === tab).Component;
    return (
        <div className="admin-page aops">
            <div className="aops-header">
                <h1>Обслуживание</h1>
                <p className="aops-muted">Состояние сервиса: проблемы, найденные по порогам, и метрики по областям.</p>
            </div>
            <div className="aops-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        className={`aops-tab ${tab === t.key ? 'aops-tab--active' : ''}`}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <Active />
        </div>
    );
}

export default AdminOps;
